import { cloneDeep } from 'lodash-es';
import type { MetricResult, MetricFetchParams } from '@/database/utils/metric.types';
import {
  type DBType,
  type MetricRequest,
  type MetricChartInfo as CommonMetricChartInfo,
  type ChartData,
} from '@/common/utils';
import { getIntervalByDbTypeAndTimePeriodTrend } from '@/database/utils/utils';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { getMetricData, getMetricBatchData } from '@/common/api/data';
import type {
  MetricV7RequestArgParamItem,
  MetricV7RequestInterval,
  MetricV7RequestPeriod,
  TagRequest,
} from '@/api-client/data/model';
import { DB_TYPE } from '@/common/define/common.define';
import { INSTANCE_ROLE } from '@/config/views/platform/database/instance/configDatabase.define';
import { applyLastTime } from '@/dashboard/features/chartDataHandler/domain/metric/applyLastTime';
import { transformData } from './transformMetric.utils';
import type { InstanceRole } from '../types';
import { DEFAULT_CHART_DATA } from './metric.define';
import { ADDITIONAL_STATS_DATA_ID } from './additionalChart.define';
import { isQueryManagerStatId, isCustomStatId } from './utils';

const TAG_KEY = 'database';
const TAG_TYPE = 'tag';
const getIntervalParam = (
  originalParam: { period: MetricV7RequestPeriod } | { fromTime: string; toTime: string },
  interval?: MetricV7RequestInterval,
): MetricV7RequestArgParamItem => {
  if ('period' in originalParam) {
    return {
      period: { value: `'${originalParam.period}'` },
      interval: { value: interval },
      applyTimeInterval: { value: 'true' },
    };
  }
  return {
    fromTime: { value: `'${originalParam.fromTime}'` },
    toTime: { value: `'${originalParam.toTime}'` },
    interval: { value: interval },
    applyTimeInterval: { value: 'true' },
  };
};
const getTargetIdParam = (instanceIds: string[]): MetricV7RequestArgParamItem => {
  return {
    targetIds: {
      tags: true,
      value: instanceIds.map((id) => `${TAG_KEY};${TAG_TYPE};${id}`).join(','),
    },
  };
};
const getCustomStatTargetParam = (instanceIds: string[]): TagRequest[] => {
  return instanceIds.map((id) => ({
    tagKey: TAG_KEY,
    tagType: TAG_TYPE,
    tagValue: [id],
  }));
};

const getMetricRequests = (
  requestFields: MetricFetchParams['requestOption'] & { dbType: NonNullable<DBType> },
): MetricRequest[] => {
  const {
    instanceIds,
    dataIds,
    category,
    summaryTypes = ['avg', 'max'],
    interpolateType = 'Null',
    interval,
    lastTime,
  } = requestFields;

  const baseRequests = dataIds!.map<MetricRequest>((dataId) => {
    const intervalParam =
      'period' in requestFields
        ? { period: requestFields.period }
        : {
            fromTime: requestFields.fromUtc0Time,
            toTime: requestFields.toUtc0Time,
          };
    const defaultInterval = getIntervalByDbTypeAndTimePeriodTrend(intervalParam);

    if (isQueryManagerStatId(dataId)) {
      const intervalArgParam = getIntervalParam(intervalParam, interval || defaultInterval);
      const targetIdArgParam = getTargetIdParam(instanceIds);

      return {
        aggregationType: 'byTarget',
        category: category || requestFields.dbType,
        dataId,
        interval: interval || defaultInterval,
        argParam: [{ ...intervalArgParam, ...targetIdArgParam }],
        targetIds: instanceIds,
        ...intervalParam,
      };
    }

    if (isCustomStatId(dataId)) {
      const targetTags = getCustomStatTargetParam(instanceIds);

      return {
        aggregationType: 'byTarget',
        category: category || requestFields.dbType,
        dataId,
        interval: interval || defaultInterval,
        tags: targetTags,
        targetIds: instanceIds,
        ...intervalParam,
      };
    }

    return {
      aggregationType: 'byTarget',
      category: category || requestFields.dbType,
      dataId,
      interval: interval || defaultInterval,
      targetIds: instanceIds,
      ...intervalParam,
    };
  });

  if (lastTime) {
    return summaryTypes.flatMap<MetricRequest>((summaryType) =>
      baseRequests.map((baseRequest) => {
        return applyLastTime(
          {
            ...baseRequest,
            summaryType,
            skipIntervalFilling: true,
          },
          lastTime,
        );
      }),
    );
  }

  return summaryTypes.flatMap<MetricRequest>((summaryType) =>
    baseRequests.map((baseRequest) => ({
      ...baseRequest,
      summaryType,
      interpolateType,
    })),
  );
};

export const metricFetchPromise = async ({
  requestOption,
  dbType,
  chartOption,
  transformOption,
  frameName = FRAME_NAMES.PA_TREND_ANALYSIS.DB_METRIC,
  signal,
}: MetricFetchParams): Promise<MetricResult> => {
  if (!requestOption.dataIds?.length) {
    return {
      data: [],
      error: null,
    };
  }

  const metricRequests = getMetricRequests({
    dbType: dbType as NonNullable<DBType>,
    ...requestOption,
  });

  const {
    data: { data = [], timeRange },
  } = await getMetricData({
    metricV7Requests: metricRequests,
    frameName,
    isPolling: true,
    signal,
  });

  return {
    ...transformData({
      data: data || [],
      transformOption,
      chartOption,
    }),
    timeRange,
  };
};

// NOTE: api 내부 로직이 batch
export const batchMetricFetchPromise = async ({
  requestOption,
  dbType,
  chartOption,
  transformOption,
  frameName = FRAME_NAMES.PA_TREND_ANALYSIS.DB_METRIC,
  signal,
}: MetricFetchParams): Promise<MetricResult> => {
  if (!requestOption.dataIds?.length) {
    return {
      data: [],
      error: null,
    };
  }

  const metricRequests = getMetricRequests({
    dbType: dbType as NonNullable<DBType>,
    ...requestOption,
  });

  const {
    data: { data = [] },
  } = await getMetricBatchData({
    metricV7Requests: metricRequests,
    frameName,
    isPolling: true,
    signal,
  });

  return transformData({
    data,
    transformOption,
    chartOption,
  });
};

export const getChartDataAndStatMap = (metricChartInfo: CommonMetricChartInfo[]) => {
  return metricChartInfo.reduce<{
    dataToReturn: ChartData;
    statMap: Record<string, string>;
  }>(
    (acc, { chartData, chartId, title }) => {
      acc.dataToReturn.data = { ...acc.dataToReturn.data, ...chartData.data };
      acc.dataToReturn.series = { ...acc.dataToReturn.series, ...chartData.series };
      if (!acc.dataToReturn.labels?.length) {
        acc.dataToReturn.labels = chartData.labels;
      }
      acc.statMap[chartId] = title;
      return acc;
    },
    { dataToReturn: cloneDeep(DEFAULT_CHART_DATA), statMap: {} },
  );
};

export const getAdditionalDataStatMap = (dataId: string) => {
  const statTitle =
    {
      [ADDITIONAL_STATS_DATA_ID.ORACLE_WAIT]: 'Wait Class',
      [ADDITIONAL_STATS_DATA_ID.ORACLE_TIME]: 'Time Model',
      [ADDITIONAL_STATS_DATA_ID.ALTIBASE_WAIT]: 'Wait Class',
      [ADDITIONAL_STATS_DATA_ID.TIBERO_WAIT]: 'Wait Class',
    }[dataId] || dataId;

  return { [dataId]: statTitle };
};

export const getPgMetricCategory = ({
  instanceRole,
}: {
  instanceRole?: InstanceRole;
}): 'postgresql' | 'cluster_postgresql' => {
  if (
    instanceRole === INSTANCE_ROLE.PG_CLUSTER_DATABASE ||
    instanceRole === INSTANCE_ROLE.PG_CLUSTER_INSTANCE
  ) {
    return 'cluster_postgresql';
  }

  return DB_TYPE.POSTGRESQL;
};
